import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { certificationStore } from "@/lib/certificationStore";
import { chapterStore } from "@/lib/chapterStore";
import { questionStore } from "@/lib/questionStore";
import { examSetStore } from "@/lib/examSetStore";
import { learningObjectiveStore } from "@/lib/learningObjectiveStore";
import { keywordStore } from "@/lib/keywordStore";
import { keywordAssignmentStore } from "@/lib/keywordAssignmentStore";
import { userKeywordProgressStore } from "@/lib/userKeywordProgressStore";
import { userKeywordResultsStore } from "@/lib/userKeywordResultsStore";
import { userProgressStore } from "@/lib/userProgressStore";
import { practiceSessionStore } from "@/lib/practiceSessionStore";
import { getStripeEnvironment } from "@/lib/stripe";
import { clearPendingCheckoutIntent } from "@/lib/pendingPlan";
import { logAuthEvent } from "@/lib/logAuthEvent";

interface Profile {
  id: string;
  full_name: string;
  subscription_tier: "free" | "monthly" | "quarterly" | "yearly";
  active_certification_id: string | null;
  created_at: string;
  updated_at: string;
  deletion_scheduled_at: string | null;
  deletion_effective_at: string | null;
  free_access_until: string | null;
  free_access_granted_by: string | null;
  free_access_granted_at: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  /**
   * True when a Stripe `subscriptions` row exists for this user in the
   * current env with active/trialing/past_due/canceled-with-future-period.
   * Used as a paywall safety net so a brief profile-tier desync (webhook
   * race or missing profile row) doesn't dump a paid user on /pricing.
   */
  hasActiveSubscription: boolean;
  /**
   * Paywall classification for the current user in the current Stripe env:
   * - 'active'   — has a currently-valid subscription row.
   * - 'expired'  — has at least one historical subscription row, but none
   *                are currently valid (e.g. canceled past period end).
   * - 'none'     — has never subscribed in this environment.
   * The internal `profile.subscription_tier` sentinel `'free'` is preserved
   * for tier-gating, but the UI uses this signal to choose copy between
   * "No Active Subscription" and "Subscription Expired".
   */
  subscriptionState: "none" | "active" | "expired";
  /**
   * Set when the user has scheduled deletion of their account. While set,
   * the app is locked behind the deletion-scheduled screen until the user
   * restores or the recovery window expires.
   */
  deletionScheduledAt: string | null;
  deletionEffectiveAt: string | null;
  isLoading: boolean;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, fullName: string, captchaToken?: string) => Promise<{ error: string | null; needsVerification: boolean }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionState, setSubscriptionState] = useState<"none" | "active" | "expired">("none");
  const [deletionScheduledAt, setDeletionScheduledAt] = useState<string | null>(null);
  const [deletionEffectiveAt, setDeletionEffectiveAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Deduplication: track which user ID is currently being hydrated
  const hydratingForRef = useRef<string | null>(null);
  // Track the latest known tier so refreshProfile can detect a tier change
  // synchronously (setProfile's functional updater runs asynchronously and
  // is therefore unreliable for triggering side-effects like store reloads).
  const tierRef = useRef<Profile["subscription_tier"] | null>(null);

  const hydrateUserSession = useCallback(async (userId: string) => {
    // Skip if we're already hydrating for this exact user
    if (hydratingForRef.current === userId) return;
    hydratingForRef.current = userId;

    try {
      // Fire-and-forget: reload data stores in background (non-blocking)
      certificationStore.reload();
      chapterStore.reload();
      examSetStore.reload();
      learningObjectiveStore.reload();
      keywordStore.reload();
      keywordAssignmentStore.reload();
      userKeywordProgressStore.loadForUser(userId);
      userKeywordResultsStore.loadForUser(userId);
      userProgressStore.loadForUser(userId);
      practiceSessionStore.loadForUser(userId);

      const env = (() => {
        try { return getStripeEnvironment(); } catch { return "sandbox" as const; }
      })();

      // Resolve all account-access signals before clearing isLoading. Routing
      // depends on the complete set; otherwise a paid user can briefly look
      // like a free learner and flash /pricing before the subscription probe
      // finishes.
      const rolesPromise = supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);
      const profilePromise = supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();
      const subPromise = supabase
        .from("subscriptions")
        .select("status,current_period_end")
        .eq("user_id", userId)
        .eq("environment", env)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      const [{ data: roleData }, { data: profileData }, { data: subRow }] = await Promise.all([
        rolesPromise,
        profilePromise,
        subPromise,
      ]);

      // Bail if a newer hydration superseded us
      if (hydratingForRef.current !== userId) return;

      const roles = Array.isArray(roleData) ? roleData.map((r) => r.role) : [];
      const userIsAdmin = roles.includes("admin") || roles.includes("super_admin");
      const userIsSuperAdmin = roles.includes("super_admin");

      setIsAdmin(userIsAdmin);
      setIsSuperAdmin(userIsSuperAdmin);
      setProfile(profileData ? (profileData as Profile) : null);
      tierRef.current = (profileData as Profile | null)?.subscription_tier ?? null;
      const state = classifySubRow(subRow);
      setHasActiveSubscription(state === "active");
      setSubscriptionState(state);
      setDeletionScheduledAt(
        (profileData as Profile | null)?.deletion_scheduled_at ?? null,
      );
      setDeletionEffectiveAt(
        (profileData as Profile | null)?.deletion_effective_at ?? null,
      );

      // Fire-and-forget: question store reload with correct table
      questionStore.reload(userIsAdmin);

      setIsLoading(false);
    } catch {
      if (hydratingForRef.current === userId) {
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setHasActiveSubscription(false);
        setSubscriptionState("none");
        setDeletionScheduledAt(null);
        setDeletionEffectiveAt(null);
        questionStore.reload(false);
        setIsLoading(false);
      }
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const env = (() => {
      try { return getStripeEnvironment(); } catch { return "sandbox" as const; }
    })();
    const [profileRes, rolesRes, subRes] = await Promise.all([
      supabase.from("profiles").select("*").eq("id", user.id).single(),
      supabase.from("user_roles").select("role").eq("user_id", user.id),
      supabase
        .from("subscriptions")
        .select("status,current_period_end")
        .eq("user_id", user.id)
        .eq("environment", env)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);
    let tierChanged = false;
    if (profileRes.data) {
      const p = profileRes.data as Profile;
      if (tierRef.current !== p.subscription_tier) tierChanged = true;
      tierRef.current = p.subscription_tier;
      setProfile(p);
      setDeletionScheduledAt(p.deletion_scheduled_at ?? null);
      setDeletionEffectiveAt(p.deletion_effective_at ?? null);
    }
    const roles = Array.isArray(rolesRes.data) ? rolesRes.data.map((r) => r.role) : [];
    setIsSuperAdmin(roles.includes("super_admin"));
    const newIsAdmin = roles.includes("admin") || roles.includes("super_admin");
    setIsAdmin(newIsAdmin);
    const state = classifySubRow(subRes.data);
    setHasActiveSubscription(state === "active");
    setSubscriptionState(state);

    // Tier-gated stores (chapters, exam_sets, questions, learning_objectives,
    // keywords, keyword_assignments) are filtered by RLS against
    // `tier_meets(auth.uid(), c.required_tier)`. When a user just upgraded
    // their subscription (e.g. returning from Stripe checkout), the initial
    // hydration loaded these stores under the OLD tier and got 0 rows.
    // Without a reload here, Chapter Practice and Mock Exams stay empty
    // even though the user now has access. Reload whenever the tier changes.
    if (tierChanged) {
      certificationStore.reload();
      chapterStore.reload();
      examSetStore.reload();
      learningObjectiveStore.reload();
      keywordStore.reload();
      keywordAssignmentStore.reload();
      questionStore.reload(newIsAdmin);
    }
  }, [user]);

  useEffect(() => {
    let initialSessionHandled = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        // If getSession already handled this, skip duplicate INITIAL_SESSION
        if (_event === "INITIAL_SESSION" && initialSessionHandled) return;

        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          const shouldHydrate = hydratingForRef.current !== newSession.user.id;
          if (shouldHydrate) setIsLoading(true);
          void hydrateUserSession(newSession.user.id);
        } else {
          hydratingForRef.current = null;
          setProfile(null);
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setHasActiveSubscription(false);
          setSubscriptionState("none");
          setDeletionScheduledAt(null);
          setDeletionEffectiveAt(null);
          setIsLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      initialSessionHandled = true;
      setSession(existingSession);
      setUser(existingSession?.user ?? null);

      if (existingSession?.user) {
        void hydrateUserSession(existingSession.user.id);
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [hydrateUserSession]);

  const signIn = async (email: string, password: string, captchaToken?: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: captchaToken ? { captchaToken } : undefined,
    });
    if (error) {
      return { error: "Invalid email or password" };
    }
    return { error: null };
  };

  const signUp = async (email: string, password: string, fullName: string, captchaToken?: string) => {
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        ...(captchaToken ? { captchaToken } : {}),
      },
    });
    if (error) {
      return { error: "Unable to create account. Please try again.", needsVerification: false };
    }
    const needsVerification = !data.session;
    return { error: null, needsVerification };
  };

  const signOut = async () => {
    const currentUserId = user?.id ?? null;
    const currentEmail = user?.email ?? null;
    hydratingForRef.current = null;
    clearPendingCheckoutIntent();
    await supabase.auth.signOut();
    logAuthEvent({
      event_type: "logout",
      user_id: currentUserId,
      email: currentEmail,
    });
    setUser(null);
    setSession(null);
    setProfile(null);
    setIsAdmin(false);
    setIsSuperAdmin(false);
    setHasActiveSubscription(false);
    setSubscriptionState("none");
    setDeletionScheduledAt(null);
    setDeletionEffectiveAt(null);
    setIsLoading(false);
  };

  // Revalidate the session against the server whenever the tab regains focus.
  // If the user was deleted/banned server-side, sign them out immediately with
  // a toast — JWTs would otherwise stay valid until their natural expiry.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const revalidate = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const { data, error } = await supabase.auth.getUser();
        if (cancelled) return;
        if (error || !data?.user) {
          await supabase.auth.signOut().catch(() => {});
          hydratingForRef.current = null;
          setUser(null);
          setSession(null);
          setProfile(null);
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setHasActiveSubscription(false);
          setSubscriptionState("none");
          setDeletionScheduledAt(null);
          setDeletionEffectiveAt(null);
          toast({
            title: "Signed out",
            description: "Your account is no longer available. Please sign in again.",
            variant: "destructive",
          });
          window.location.replace("/login");
        }
      } catch {
        /* network blip — ignore */
      }
    };

    document.addEventListener("visibilitychange", revalidate);
    window.addEventListener("focus", revalidate);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", revalidate);
      window.removeEventListener("focus", revalidate);
    };
  }, [user]);

  return (
    <AuthContext.Provider
      value={{ user, session, profile, isAdmin, isSuperAdmin, hasActiveSubscription, subscriptionState, deletionScheduledAt, deletionEffectiveAt, isLoading, signIn, signUp, signOut, refreshProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Mirror of the server-side `has_active_subscription` predicate, extended
// to distinguish "never subscribed" (no row) from "expired" (historical
// row that no longer qualifies as active).
function classifySubRow(
  row: { status?: string | null; current_period_end?: string | null } | null | undefined,
): "none" | "active" | "expired" {
  if (!row || !row.status) return "none";
  const periodEnd = row.current_period_end ? new Date(row.current_period_end) : null;
  const periodOk = !periodEnd || periodEnd > new Date();
  if (["active", "trialing", "past_due"].includes(row.status) && periodOk) return "active";
  if (row.status === "canceled" && periodEnd && periodEnd > new Date()) return "active";
  return "expired";
}
